import { useAtomValue, useSetAtom } from 'jotai';
import React from 'react';

import { Button, FlexContainer, ModalManager } from '@placer-ui/components';
import {
    resultsTableFiltersStore,
    supportCreateTagOpenStore,
    supportTableTagIdStore,
    supportTagToUpdateStore,
} from 'features/my-zone/support-requests/store/property-store';
import type { RequestPropertyFormProps } from 'features/my-zone/support-requests/components/support-requests-modals/modals/request-new-location/request-property/types';

export const useTableConfirmationModals = ({
    onSkipModal,
}: {
    onSkipModal: (type: string, onNextHandler: RequestPropertyFormProps['onNextHandler']) => void;
}) => {
    const supportTableTagId = useAtomValue(supportTableTagIdStore);
    const setSupportCreateTagOpen = useSetAtom(supportCreateTagOpenStore);
    const setTagToUpdate = useSetAtom(supportTagToUpdateStore);
    const setFilters = useSetAtom(resultsTableFiltersStore);

    const modalManager = ModalManager.getInstance();

    const handleMissingInfoModalClicked = () => {
        setFilters((prev) => {
            return Object.keys(prev).reduce((acc, key) => {
                return { ...acc, [key]: key === 'MissingInfo' };
            }, {});
        });

        modalManager.removeModal('continue-with-missing-info');
    };

    const handleSaveAsTag = () => {
        setSupportCreateTagOpen(true);
        modalManager.removeModal('save-as-tag');
    };

    const handleUpdateTag = () => {
        setTagToUpdate(supportTableTagId);
        modalManager.removeModal('update-tag');
    };

    const handleSkipModal = (
        type: string,
        onNextHandler: RequestPropertyFormProps['onNextHandler'],
    ) => {
        modalManager.removeModal(type);
        onSkipModal(type, onNextHandler);
    };

    const showSaveAsTagModal = (onNextHandler: RequestPropertyFormProps['onNextHandler']) => {
        modalManager.addModal({
            type: 'confirmation',
            id: 'save-as-tag',
            testId: 'save-as-tag-modal',
            content:
                'Saving it as a tag lets you quickly access it later, \ngain insights and bookmark for easy reference.',
            illustration: 'TagIllustration',
            title: 'Save as Tag?',
            overlayOffset: 2,
            actionsOrientation: 'vertical',
            actions: (
                <FlexContainer gap="8px">
                    <Button
                        fullWidth
                        variant="secondary-blue"
                        testId="skip-save-as-tag-button"
                        onClick={() => handleSkipModal('save-as-tag', onNextHandler)}
                    >
                        Skip
                    </Button>
                    <Button
                        fullWidth
                        variant="primary-brand"
                        testId="save-as-tag-button"
                        onClick={handleSaveAsTag}
                    >
                        Save as Tag
                    </Button>
                </FlexContainer>
            ),
        });
    };

    const showUpdateTagModal = (onNextHandler: RequestPropertyFormProps['onNextHandler']) => {
        modalManager.addModal({
            type: 'confirmation',
            id: 'update-tag',
            testId: 'update-tag-modal',
            content:
                "You updated the list, but the tag hasn't been updated. Would you like to update it before continuing?",
            illustration: 'TagIllustration',
            title: 'Update Tag?',
            overlayOffset: 2,
            actionsOrientation: 'vertical',
            actions: (
                <FlexContainer gap="8px">
                    <Button
                        fullWidth
                        variant="secondary-blue"
                        testId="skip-update-tag-button"
                        onClick={() => handleSkipModal('update-tag', onNextHandler)}
                    >
                        Skip
                    </Button>
                    <Button
                        fullWidth
                        variant="primary-brand"
                        testId="update-tag-button"
                        onClick={handleUpdateTag}
                    >
                        Update Tag
                    </Button>
                </FlexContainer>
            ),
        });
    };

    const showMissingInfoModal = (onNextHandler: RequestPropertyFormProps['onNextHandler']) => {
        modalManager.addModal({
            type: 'confirmation',
            id: 'continue-with-missing-info',
            testId: 'missing-info-modal',
            content: 'The support team will not review these properties. Continue without them?',
            illustration: 'ReportDailyFailedIllustration',
            title: 'Submit with Missing Information?',
            overlayOffset: 2,
            actionsOrientation: 'vertical',
            actions: (
                <FlexContainer gap="8px">
                    <Button
                        fullWidth
                        variant="secondary-gray"
                        testId="submit-with-missing-info-button"
                        onClick={() => {
                            handleSkipModal('continue-with-missing-info', onNextHandler);
                        }}
                    >
                        Submit Ticket
                    </Button>
                    <Button
                        fullWidth
                        variant="primary-gray"
                        testId="filter-missing-info-button"
                        onClick={handleMissingInfoModalClicked}
                    >
                        Edit Missing Info
                    </Button>
                </FlexContainer>
            ),
        });
    };

    return {
        showMissingInfoModal,
        showSaveAsTagModal,
        showUpdateTagModal,
    };
};
